import { createAutocomplete } from "@algolia/autocomplete-core";
import type { AutocompleteSource, AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { CommonCMS } from "@lib/constants/contentful";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useStore } from "@store/index";
import { useEffect, useMemo, useRef, useState } from "react";
import { debouncePromise } from "./debouncedUtil";
import { useLocale } from "next-intl";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { usePathname } from "next/navigation";
import { useRouter } from "i18n/routing";

const DEBOUNCE_MS = 450;
export const debounced = debouncePromise(
    (items: AutocompleteSource<BaseItem>[]) => Promise.resolve(items),
    DEBOUNCE_MS
);
interface UseSearchBarProps {
    searchBarOpen?: boolean;
    isDesktopView?: boolean;
}
type CustomAutoCompleteState = AutocompleteState<BaseItem> & { totalHits?: number };

export const useSearchBar = ({ searchBarOpen, isDesktopView }: UseSearchBarProps) => {
    const { push } = useRouter();
    const pathname = usePathname();
    const locale = useLocale();
    const { searchClient, indexName } = useAlgoliaClient(locale);
    const [autocompleteState, setAutocompleteState] = useState<CustomAutoCompleteState>({
        isOpen: false,
        collections: [],
        query: "",
        completion: null,
        totalHits: undefined,
        context: {},
        activeItemId: undefined,
        status: "idle",
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { get: getMicrocopy } = useMicrocopy();
    const { hideModal } = useGlobalModalContext();
    const containerRef = useRef(null);
    const formRef = useRef(null);
    const inputRef = useRef(null);
    const searchPagePath = useMemo(() => {
        return getPagePathByLocale(searchPage, locale);
    }, [locale]);
    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
    }, []);
    useEffect(() => {
        if (isDesktopView) {
            if (searchBarOpen) {
                inputRef?.current?.focus();
                setAutocompleteState((prevState) => ({
                    ...prevState,
                    isOpen: true,
                }));
            } else {
                inputRef?.current?.blur();
                setAutocompleteState((prevState) => ({
                    ...prevState,
                    isOpen: false,
                }));
            }
        }
    }, [searchBarOpen, isDesktopView]);

    useEffect(() => {
        if (isLoading) {
            setIsLoading(false);
            hideModal?.();
        }
    }, [pathname, isLoading]);

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
        key: "RECENT_SEARCH",
        limit: 5,
        transformSource({ source }) {
            return {
                ...source,
                getItemUrl({ item }) {
                    return `${searchPagePath}?query=${encodeURIComponent(item.label)}`;
                },
            };
        },
    });
    const currentUser = useStore((state) => state.data.me);
    const userToken = getAlgoliaUserToken(currentUser);
    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
        searchClient,
        indexName: `${indexName}_query_suggestions`,
        getSearchParams() {
            return recentSearchesPlugin?.data?.getAlgoliaSearchParams({
                length: 5,
                offset: 0,
                highlightPreTag: "<mark>",
                highlightPostTag: "</mark>",
                userToken: userToken,
            });
        },
    });
    const autocomplete = useMemo(
        () =>
            createAutocomplete({
                id: "search-autocomplete",
                placeholder: getMicrocopy(CommonCMS.shared, CommonCMS.searchBarPlaceholder),
                openOnFocus: true,
                autoFocus: true,
                plugins: [recentSearchesPlugin, querySuggestionsPlugin],
                initialState: autocompleteState,

                onStateChange({ state }) {
                    const { context = {}, ...rest } = state;
                    setAutocompleteState((prev) => ({
                        ...prev,
                        ...rest,
                        context,
                        totalHits: context.totalHits as number,
                    }));
                },
                stallThreshold: 1000,
                onSubmit(params) {
                    setIsLoading(true);
                    setAutocompleteState((prev) => ({
                        ...prev,
                        isOpen: false,
                    }));
                    params?.setQuery(params.state.query);
                    const q = params?.state?.query?.trim();
                    const searchUrl = q
                        ? `${searchPagePath}?query=${encodeURIComponent(q)}`
                        : searchPagePath;
                    push(searchUrl);
                    params?.refresh();
                },
                getSources({ query, setContext }) {
                    if (!query) return [];

                    return debounced([
                        {
                            sourceId: "products",
                            getItemInputValue({ item }) {
                                return item?.query as string;
                            },
                            getItems({ query }) {
                                return getAlgoliaResults({
                                    searchClient,
                                    queries: [
                                        {
                                            indexName,
                                            query,
                                            params: {
                                                hitsPerPage: 3,
                                                facetFilters: ["hasStock:true"],
                                                userToken: userToken,
                                                clickAnalytics: true,
                                            },
                                        },
                                    ],
                                    transformResponse({ results, hits }) {
                                        setContext({ totalHits: results?.[0]?.nbHits });
                                        return hits;
                                    },
                                });
                            },
                        },
                    ]);
                },
                // @ts-ignore
                container: formRef?.current,
            }),

        [userToken, isDesktopView]
    );

    return {
        autocomplete,
        setAutocompleteState,
        recentSearchesPlugin,
        querySuggestionsPlugin,
        containerRef,
        formRef,
        inputRef,
        isLoading,
        setIsLoading,
        searchClient,
        autocompleteState,
    };
};
